/**
 * Subject Helper Utilities
 * 
 * Helper functions to check if a teacher has specific subjects
 * based on their primarySubject and teachingSubjects configuration.
 */

import { Teacher } from '../context/TeacherContext';

/**
 * Check if a teacher has a specific subject
 * 
 * @param teacher - The teacher object
 * @param subject - The subject to check for
 * @returns true if the teacher has the subject as primary or in teachingSubjects
 */
export function hasSubject(teacher: Teacher | null, subject: string): boolean {
  if (!teacher) return false;
  
  // Check primarySubject
  if (teacher.primarySubject === subject) return true;
  
  // For ELA teachers, also consider them as having Reading
  if (subject === 'Reading' && teacher.primarySubject === 'ELA') return true;
  if (subject === 'ELA' && teacher.primarySubject === 'Reading') return true;
  
  // Check teachingSubjects array
  if (teacher.teachingSubjects?.some(
    ts => ts.subject === subject && ts.isActive !== false
  )) return true;
  
  // Also check teachingSubjects for ELA/Reading equivalence
  if (subject === 'Reading') {
    if (teacher.teachingSubjects?.some(
      ts => ts.subject === 'ELA' && ts.isActive !== false
    )) return true;
  }
  if (subject === 'ELA') {
    if (teacher.teachingSubjects?.some(
      ts => ts.subject === 'Reading' && ts.isActive !== false
    )) return true;
  }
  
  return false;
}

/**
 * Check if a teacher has Reading/ELA as a subject
 * 
 * @param teacher - The teacher object
 * @returns true if the teacher teaches Reading or ELA
 */
export function hasReading(teacher: Teacher | null): boolean {
  return hasSubject(teacher, 'Reading') || hasSubject(teacher, 'ELA');
}

/**
 * Check if a teacher has Math as a subject
 * 
 * @param teacher - The teacher object
 * @returns true if the teacher teaches Math
 */
export function hasMath(teacher: Teacher | null): boolean {
  return hasSubject(teacher, 'Math');
}

/**
 * Get all subjects a teacher has
 * 
 * @param teacher - The teacher object
 * @returns Array of subject strings
 */
export function getTeacherSubjects(teacher: Teacher | null): string[] {
  if (!teacher) return [];
  
  const subjects: Set<string> = new Set();
  
  // Add primary subject
  if (teacher.primarySubject) {
    subjects.add(teacher.primarySubject);
  }
  
  // Add teaching subjects
  if (teacher.teachingSubjects) {
    teacher.teachingSubjects.forEach(ts => {
      if (ts.subject && ts.isActive !== false) {
        subjects.add(ts.subject);
      }
    });
  }
  
  return Array.from(subjects);
}
